import React from 'react'
import PropTypes from 'prop-types'
import * as impersonationService from '../../services/impersonations'
import './ImpersonationSelector.css'

class ImpersonationSelector extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      availableGames: [],
      loading: false,
      searchTerm: '',
      pagination: {
        current_page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0
      },
      notification: null
    }
    this.searchTimeout = null
    this.notificationTimeout = null
  }

  componentDidMount() {
    if (this.props.canImpersonate) {
      this.loadAvailableGames()
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.canImpersonate !== this.props.canImpersonate && this.props.canImpersonate) {
      this.loadAvailableGames()
    }
  }

  componentWillUnmount() {
    // Clean up timeouts to prevent memory leaks
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout)
    }
  }

  showNotification = (message, type = 'info') => {
    // Clear any existing timeout to prevent multiple timeouts
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout)
    }

    this.setState({ notification: { message, type } })
    // Auto-hide notification after 5 seconds
    this.notificationTimeout = setTimeout(() => {
      this.setState({ notification: null })
    }, 5000)
  }

  clearNotification = () => {
    this.setState({ notification: null })
  }

  handleImpersonateClick = (event) => {
    const { gameId, userId, characterId } = event.currentTarget.dataset
    this.handleImpersonateUser(userId, gameId, characterId || null)
  }

  loadAvailableGames = async () => {
    this.setState({ loading: true })
    try {
      const data = await impersonationService.getAvailableGames(
        this.state.pagination.current_page,
        this.state.pagination.per_page,
        this.state.searchTerm
      )
      this.setState({
        availableGames: data.games,
        pagination: data.pagination,
        loading: false
      })
    } catch (error) {
      console.error('Error loading available games:', error)
      this.showNotification(`Error loading games: ${error.message}`, 'error')
      this.setState({ loading: false })
    }
  }

  handleSearchChange = (event) => {
    const searchTerm = event.target.value

    // Update UI immediately for responsive feel
    this.setState({ searchTerm })

    // Clear any existing timeout to prevent multiple API calls
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    // Debounce only the API call, not the UI update
    this.searchTimeout = setTimeout(() => {
      this.setState({
        pagination: { ...this.state.pagination, current_page: 1 }
      }, this.loadAvailableGames)
    }, 300) // 300ms delay for good UX balance
  }

  handlePageChange = (newPage) => {
    const { total_pages } = this.state.pagination
    if (newPage < 1 || (total_pages && newPage > total_pages)) return
    this.setState({
      pagination: { ...this.state.pagination, current_page: newPage }
    }, this.loadAvailableGames)
  }

  handleImpersonateUser = async (userId, gameId, characterId) => {
    try {
      await impersonationService.create(userId, gameId, characterId)
      // Only redirect after successful impersonation
      window.location.href = `/games/${gameId}`
    } catch (error) {
      console.error('Impersonation failed:', error)

      // Provide user-friendly error feedback
      const errorMessage = error.message || 'Failed to start impersonation'
      this.showNotification(`Impersonation failed: ${errorMessage}`, 'error')
      // Don't redirect on error - let user see the error and try again if needed
    }
  }

  render() {
    const { canImpersonate } = this.props
    const { availableGames, loading, searchTerm, pagination, notification } = this.state

    if (!canImpersonate) return null

    // Pre-compute character maps for all games to avoid repeated calculations
    const characterMapsByGameId = availableGames.reduce((gameMaps, game) => {
      gameMaps[game.id] = game.characters.reduce((charMap, character) => {
        charMap[character.user_id] = character
        return charMap
      }, {})
      return gameMaps
    }, {})

    return (
      <div className="ImpersonationSelector">
        {notification && (
          <div className={`ImpersonationSelector__notification ImpersonationSelector__notification--${notification.type}`}>
            <span className="ImpersonationSelector__notificationMessage">{notification.message}</span>
            <button
              className="ImpersonationSelector__notificationClose"
              onClick={this.clearNotification}
            >
              ×
            </button>
          </div>
        )}
        <div className="ImpersonationSelector__header">
          <h3>Developer Impersonation</h3>
          <p>Select a game and user to impersonate for debugging purposes</p>
        </div>
        <div className="ImpersonationSelector__search">
          <input
            type="text"
            id="impersonation-search"
            name="impersonation-search"
            placeholder="Search games..."
            value={searchTerm}
            onChange={this.handleSearchChange}
            className="ImpersonationSelector__searchInput"
          />
        </div>
        {loading ? (
          <div className="ImpersonationSelector__loading">Loading games...</div>
        ) : (
          <>
            <div className="ImpersonationSelector__games">
              {availableGames.length === 0 && <div>No games found.</div>}
              {availableGames.map(game => {
                const charactersByUserId = characterMapsByGameId[game.id]

                return (
                  <div key={game.id} className="ImpersonationSelector__game">
                    <div className="ImpersonationSelector__gameHeader">
                      <h4>{game.name}</h4>
                      <span className="ImpersonationSelector__gameStats">
                        {game.character_count} characters
                      </span>
                    </div>
                    <div className="ImpersonationSelector__users">
                      {game.users.map(user => {
                        const userCharacter = charactersByUserId[user.id]
                        return (
                          <div key={user.id} className="ImpersonationSelector__user">
                            <span className="ImpersonationSelector__username">{user.username}</span>
                            <button
                              className="ImpersonationSelector__impersonateButton"
                              data-game-id={game.id}
                              data-user-id={user.id}
                              data-character-id={userCharacter?.id || ''}
                              onClick={this.handleImpersonateClick}
                            >
                              Impersonate
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
            {pagination.total_pages > 1 && (
              <div className="ImpersonationSelector__pagination">
                <button
                  onClick={() => this.handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page <= 1}
                  className="ImpersonationSelector__pageButton"
                >
                  Previous
                </button>
                <span className="ImpersonationSelector__pageInfo">
                  Page {pagination.current_page} of {pagination.total_pages}
                </span>
                <button
                  onClick={() => this.handlePageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page >= pagination.total_pages}
                  className="ImpersonationSelector__pageButton"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    )
  }
}

ImpersonationSelector.propTypes = {
  canImpersonate: PropTypes.bool.isRequired
}

export default ImpersonationSelector